import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { adminService } from '../../services/api';
import { useMemo } from 'react';

const Masters = () => {
  const [masters, setMasters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedMaster, setSelectedMaster] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    specialization: '',
    experience: '',
    status: 'active'
  });
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'İsim alanı zorunludur';
    if (!formData.email.trim()) errors.email = 'E-posta alanı zorunludur';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Geçerli bir e-posta adresi giriniz';
    if (!formData.phone.trim()) errors.phone = 'Telefon alanı zorunludur';
    if (!formData.specialization.trim()) errors.specialization = 'Uzmanlık alanı zorunludur';
    if (!formData.experience) errors.experience = 'Deneyim alanı zorunludur';
    else if (isNaN(formData.experience) || formData.experience < 0) errors.experience = 'Geçerli bir deneyim süresi giriniz';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const fetchMasters = async () => {
    try {
      setLoading(true);
      const response = await adminService.getMechanics();
      setMasters(response.data.data.mechanics);
      setError(null);
    } catch (err) {
      setError('Ustalar yüklenirken bir hata oluştu.');
      setSnackbar({
        open: true,
        message: 'Ustalar yüklenirken bir hata oluştu.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMasters();
  }, []);

  const handleOpenDialog = (master = null) => {
    if (master) {
      setSelectedMaster(master);
      setFormData({
        name: master.name,
        email: master.email,
        phone: master.phone,
        specialization: master.speciality,
        experience: master.experience,
        status: master.isActive ? 'active' : 'inactive'
      });
    } else {
      setSelectedMaster(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        specialization: '',
        experience: '',
        status: 'active'
      });
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedMaster(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      specialization: '',
      experience: '',
      status: 'active'
    });
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      if (selectedMaster) {
        await adminService.updateMaster(selectedMaster.id, formData);
        setSnackbar({
          open: true,
          message: 'Usta başarıyla güncellendi.',
          severity: 'success'
        });
      } else {
        await adminService.createMaster(formData);
        setSnackbar({
          open: true,
          message: 'Yeni usta başarıyla eklendi.',
          severity: 'success'
        });
      }
      handleCloseDialog();
      fetchMasters();
    } catch (err) {
      setError('İşlem sırasında bir hata oluştu.');
      setSnackbar({
        open: true,
        message: 'İşlem sırasında bir hata oluştu.',
        severity: 'error'
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu ustayı pasife almak istediğinizden emin misiniz?')) {
      try {
        await adminService.deleteMaster(id);
        setSnackbar({
          open: true,
          message: 'Usta başarıyla pasife alındı.',
          severity: 'success'
        });
        fetchMasters();
      } catch (err) {
        setError('Usta pasife alınırken bir hata oluştu.');
        setSnackbar({
          open: true,
          message: 'Usta pasife alınırken bir hata oluştu.',
          severity: 'error'
        });
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'busy':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Aktif';
      case 'inactive':
        return 'Pasif';
      case 'busy':
        return 'Meşgul';
      default:
        return status;
    }
  };

  // Filtrelenmiş ve aranmış ustalar
  const filteredMasters = useMemo(() => {
    return masters.filter(master => {
      // Arama
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        master.name.toLowerCase().includes(search) ||
        (master.email && master.email.toLowerCase().includes(search)) ||
        (master.phone && master.phone.toLowerCase().includes(search)) ||
        (master.speciality && master.speciality.toLowerCase().includes(search));
      // Durum
      let matchesStatus = true;
      if (statusFilter === 'active') {
        matchesStatus = master.isActive === true;
      } else if (statusFilter === 'inactive') {
        matchesStatus = master.isActive === false;
      } else if (statusFilter === 'busy') {
        matchesStatus = master.status === 'busy';
      }
      return matchesSearch && matchesStatus;
    });
  }, [masters, searchTerm, statusFilter]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Başlık ve aksiyonlar */}
      <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems={isMobile ? 'stretch' : 'center'} mb={3} gap={isMobile ? 2 : 0}>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" fontWeight={600}>
          Usta Yönetimi
        </Typography>
        <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={isMobile ? 1 : 0}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchMasters}
            sx={{ mr: isMobile ? 0 : 1, mb: isMobile ? 1 : 0, width: isMobile ? '100%' : 'auto' }}
            size={isMobile ? 'large' : 'medium'}
            fullWidth={isMobile}
          >
            Yenile
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            size={isMobile ? 'large' : 'medium'}
            fullWidth={isMobile}
          >
            Yeni Usta
          </Button>
        </Box>
      </Box>

      {/* Arama ve filtre alanları */}
      <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={2} mb={3} alignItems={isMobile ? 'stretch' : 'center'}>
        <TextField
          placeholder="İsim, e-posta, telefon veya uzmanlık ara..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          size={isMobile ? 'large' : 'small'}
          fullWidth={isMobile}
        />
        <FormControl fullWidth={isMobile} size={isMobile ? 'large' : 'small'}>
          <InputLabel>Durum</InputLabel>
          <Select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            label="Durum"
          >
            <MenuItem value="all">Tümü</MenuItem>
            <MenuItem value="active">Aktif</MenuItem>
            <MenuItem value="inactive">Pasif</MenuItem>
            <MenuItem value="busy">Meşgul</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, fontWeight: 500, fontSize: isMobile ? 14 : 16 }}>
          {error}
        </Alert>
      )}

      {/* Masaüstü Tablo */}
      {!isMobile && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Usta</TableCell>
                <TableCell>İletişim</TableCell>
                <TableCell>Uzmanlık</TableCell>
                <TableCell>Deneyim</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMasters.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, fontWeight: 500, color: 'text.secondary' }}>
                    Kriterlere uygun usta bulunamadı.
                  </TableCell>
                </TableRow>
              ) : (
                filteredMasters.map((master) => (
                  <TableRow key={master.id} sx={{ '&:hover': { background: 'action.hover' } }}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ width: 40, height: 40 }}>
                          <PersonIcon />
                        </Avatar>
                        <Typography variant="subtitle1" fontWeight={600}>{master.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{master.email}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {master.phone}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={master.speciality} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip label={`${master.experience} yıl`} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(master.isActive ? 'active' : 'inactive')}
                        color={getStatusColor(master.isActive ? 'active' : 'inactive')}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Düzenle">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(master)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sil">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(master.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Mobil Kart/Kutu Görünümü */}
      {isMobile && (
        <Box>
          {filteredMasters.length === 0 ? (
            <Alert severity="info" sx={{ my: 4, fontWeight: 500, fontSize: 15 }}>
              Kriterlere uygun usta bulunamadı.
            </Alert>
          ) : (
            filteredMasters.map((master) => (
              <Card key={master.id} sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: 2 }}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                  <Avatar sx={{ width: 40, height: 40 }}>
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>{master.name}</Typography>
                    <Box display="flex" gap={1} mt={0.5}>
                      <Chip
                        label={getStatusLabel(master.isActive ? 'active' : 'inactive')}
                        color={getStatusColor(master.isActive ? 'active' : 'inactive')}
                        size="small"
                      />
                    </Box>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" mb={0.5}>{master.email}</Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>{master.phone}</Typography>
                <Box display="flex" gap={1} mb={1}>
                  <Chip label={master.speciality} size="small" />
                  <Chip label={`${master.experience} yıl`} size="small" />
                </Box>
                <Box display="flex" gap={1} mt={1}>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenDialog(master)}
                    size="small"
                    fullWidth
                  >
                    Düzenle
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDelete(master.id)}
                    size="small"
                    fullWidth
                  >
                    Sil
                  </Button>
                </Box>
              </Card>
            ))
          )}
        </Box>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedMaster ? 'Usta Düzenle' : 'Yeni Usta Ekle'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="İsim"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="E-posta"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Telefon"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  error={!!formErrors.phone}
                  helperText={formErrors.phone}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Uzmanlık Alanı"
                  name="specialization"
                  value={formData.specialization}
                  onChange={handleInputChange}
                  error={!!formErrors.specialization}
                  helperText={formErrors.specialization}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Deneyim (Yıl)"
                  name="experience"
                  type="number"
                  value={formData.experience}
                  onChange={handleInputChange}
                  error={!!formErrors.experience}
                  helperText={formErrors.experience}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Durum</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    label="Durum"
                  >
                    <MenuItem value="active">Aktif</MenuItem>
                    <MenuItem value="inactive">Pasif</MenuItem>
                    <MenuItem value="busy">Meşgul</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {selectedMaster ? 'Güncelle' : 'Ekle'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Masters; 